import { useCallback, useEffect, useRef } from "react";

function findScrollContainer(el: HTMLElement | null): HTMLElement | null {
  let current: HTMLElement | null = el;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return current;
    current = current.parentElement;
  }
  return null;
}

/**
 * Keeps a focused input visible when the mobile keyboard opens, scrolling the
 * nearest scrollable ancestor only the minimum distance required. Restores the
 * previous scroll position once the keyboard closes.
 */
export function useInputKeyboardScroll<T extends HTMLElement = HTMLInputElement>() {
  const ref = useRef<T>(null);
  const savedScrollRef = useRef<number>(0);
  const keyboardOpenRef = useRef(false);

  const restoreScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const container = findScrollContainer(el);
    if (!container) return;
    container.scrollTo({ top: savedScrollRef.current, behavior: "smooth" });
  }, []);

  const scrollIntoView = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const container = findScrollContainer(el);
    if (!container) return;

    // Store the current scroll position so we can restore it when the keyboard
    // closes. If the user has already scrolled while the keyboard was open we
    // still restore the pre-keyboard position, which matches the requested
    // "natural scroll position" behavior.
    savedScrollRef.current = container.scrollTop;

    const inputRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const padding = 24;

    const visibleTop = containerRect.top + padding;
    const visibleBottom = containerRect.bottom - padding;

    let delta = 0;
    if (inputRect.top < visibleTop) {
      delta = inputRect.top - visibleTop;
    } else if (inputRect.bottom > visibleBottom) {
      delta = inputRect.bottom - visibleBottom;
    }

    if (delta !== 0) {
      container.scrollBy({ top: delta, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      const keyboardOpen = window.innerHeight - vv.height > 80;
      if (keyboardOpen) {
        keyboardOpenRef.current = true;
      } else if (keyboardOpenRef.current) {
        keyboardOpenRef.current = false;
        restoreScroll();
      }
    };

    vv.addEventListener("resize", handleResize, { passive: true });
    return () => vv.removeEventListener("resize", handleResize);
  }, [restoreScroll]);

  const onFocus = useCallback(() => {
    // Wait for the mobile keyboard and any sheet-lift animation to settle
    // before measuring and scrolling.
    window.setTimeout(() => scrollIntoView(), 250);
  }, [scrollIntoView]);

  return { ref, onFocus };
}
